import { ApiClient } from '../api/ApiClient';
import { API_ENDPOINTS } from '../api/config';
import { RedditMentionDto } from '../../domain/entities/RedditMention';
import { PaginatedResult } from '../../domain/common/PaginatedResult';

/**
 * Query parameters for fetching pending mentions
 * Sprint 037 Session 9: Customer self-confirmation UI
 */
export interface PendingMentionsQuery {
  pageNumber?: number;
  pageSize?: number;
}

/**
 * Response from customer confirm/reject endpoints
 */
export interface CustomerMentionActionResponse {
  message: string;
  mentionId: string;
  action: 'confirmed' | 'rejected';
  timestamp: string;
}

/**
 * Request body for rejecting a mention
 */
export interface RejectMentionRequest {
  reason: string;
  additionalComments?: string;
}

/**
 * CustomerMentionRepository - Repository for customer self-confirmation operations
 *
 * Sprint 037 Session 9: Provides API methods for business owners to:
 * - Fetch pending high-confidence mentions (≥50%)
 * - Confirm mentions as belonging to their business
 * - Reject mentions with required reason
 *
 * Uses dedicated customer-facing endpoints that:
 * - Filter to high-confidence mentions only
 * - Create audit records for all actions
 * - Differ from admin approve/reject endpoints
 */
export class CustomerMentionRepository {
  constructor(private readonly apiClient: ApiClient) {}

  /**
   * Get pending mentions for a business
   *
   * Returns mentions that are:
   * - Matched to this business
   * - High confidence (≥50%)
   * - Pending customer confirmation
   *
   * @param businessId - The business ID to fetch mentions for
   * @param pageNumber - Page number (1-indexed, default: 1)
   * @param pageSize - Results per page (default: 10)
   */
  async getPendingMentions(
    businessId: string,
    pageNumber: number = 1,
    pageSize: number = 10
  ): Promise<PaginatedResult<RedditMentionDto>> {
    return this.apiClient.get<PaginatedResult<RedditMentionDto>>(
      API_ENDPOINTS.CUSTOMER_MENTIONS.PENDING(businessId),
      {
        params: {
          pageNumber,
          pageSize
        }
      }
    );
  }

  /**
   * Confirm a mention as belonging to the business
   *
   * Sets the mention to "CustomerConfirmed" status and creates an audit record.
   *
   * @param businessId - The business ID
   * @param mentionId - The mention ID to confirm
   */
  async confirmMention(
    businessId: string,
    mentionId: string
  ): Promise<CustomerMentionActionResponse> {
    return this.apiClient.post<CustomerMentionActionResponse>(
      API_ENDPOINTS.CUSTOMER_MENTIONS.CONFIRM(businessId, mentionId)
    );
  }

  /**
   * Reject a mention as not belonging to the business
   *
   * Sets the mention to "CustomerRejected" status and creates an audit record
   * with the rejection reason.
   *
   * @param businessId - The business ID
   * @param mentionId - The mention ID to reject
   * @param reason - Required reason for rejection (from predefined options)
   * @param additionalComments - Optional free text for additional context
   */
  async rejectMention(
    businessId: string,
    mentionId: string,
    reason: string,
    additionalComments?: string
  ): Promise<CustomerMentionActionResponse> {
    const request: RejectMentionRequest = {
      reason,
      ...(additionalComments && { additionalComments })
    };

    return this.apiClient.post<CustomerMentionActionResponse>(
      API_ENDPOINTS.CUSTOMER_MENTIONS.REJECT(businessId, mentionId),
      request
    );
  }
}
